import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import './App.css';
import ProtectedRoute from './components/ProtectedRoute';
import SmartRedirect from './components/SmartRedirect';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Accounts from './pages/Users/Accounts';
import Roles from './pages/Users/Roles';
import ApprovalAccounts from './pages/Users/ApprovalAccounts';
import AccountManagement from './pages/Users/AccountManagement';
import Units from './pages/Catalog/Units';
import Vehicles from './pages/Catalog/Vehicles';
import EntryExitReasons from './pages/Catalog/EntryExitReasons';
import Positions from './pages/Catalog/Positions';
import VisitTypes from './pages/Catalog/VisitTypes';
import IdentityTypes from './pages/Catalog/IdentityTypes';
import VerifyModes from './pages/Catalog/VerifyModes';
import Licenses from './pages/LicenseManagement/Licenses';
import FirmwareVersions from './pages/LicenseManagement/FirmwareVersions';
import LicenseUnits from './pages/LicenseManagement/LicenseUnits';
import SharedAccessManagement from './pages/LicenseManagement/SharedAccessManagement';
import Devices from './pages/DeviceManagement/Devices';
import DeviceConfiguration from './pages/DeviceManagement/DeviceConfiguration';
import AccessReport from './pages/Reports/AccessReport';
import DeviceReport from './pages/Reports/DeviceReport';
import DatabaseAnalysis from './pages/Reports/DatabaseAnalysis';
import ViolationStatistics from './pages/Reports/ViolationStatistics';
import BackupLogs from './pages/Reports/BackupLogs';
import LGSPIntegration from './pages/Reports/LGSPIntegration';
import VNeIDStatistics from './pages/Statistics/VNeIDStatistics';
import Settings from './pages/System/Settings';
import SystemLogs from './pages/System/SystemLogs';
import AlertConfigurations from './pages/System/AlertConfigurations';
import SyncData from './pages/Backup/SyncData';
import SystemBackup from './pages/Backup/SystemBackup';
import UnitsBackup from './pages/Backup/UnitsBackup';
import BTL86Backup from './pages/Backup/BTL86Backup';
import BackupLogsPage from './pages/Backup/BackupLogs';
import NotFound from './pages/NotFound';
import { NotificationProvider } from './components/Notification';
import { fetchMeRequest } from './pages/Login/slice';
import authService from './services/authService';

// Create Material UI theme
const theme = createTheme({
    palette: {
        primary: {
            main: '#2d8659', // Your brand green color
        },
        secondary: {
            main: '#f0a531', // Your brand orange color
        },
    },
    typography: {
        fontFamily: ['-apple-system', 'BlinkMacSystemFont', '"Segoe UI"', 'Roboto', '"Helvetica Neue"', 'Arial', 'sans-serif'].join(','),
    },
});

function AppContent() {
    const dispatch = useDispatch();
    const location = useLocation();
    const isLoginPage = location.pathname === '/login';

    // Fetch current user data and permissions on mount if authenticated
    // This ensures permissions are refreshed from /me endpoint on page load/refresh
    useEffect(() => {
        // Only fetch if user is authenticated (has token) AND not on login page
        // Login page handles its own session clearing
        if (authService.isAuthenticated() && !isLoginPage) {
            dispatch(fetchMeRequest());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoginPage]); // Run on mount and when navigating to/from login

    return (
        <div className="App" style={isLoginPage ? { height: '100vh', overflow: 'hidden' } : {}}>
            {!isLoginPage && <Sidebar />}
            <div className={isLoginPage ? '' : 'main-content-with-sidebar'}>
                {!isLoginPage && <Header />}
                <div className={isLoginPage ? '' : 'page-content'}>
                    <Routes>
                        {/* Public route - no authentication required */}
                        <Route path="/login" element={<Login />} />

                        {/* Protected routes - authentication required */}
                        <Route
                            path="/"
                            element={
                                <ProtectedRoute>
                                    <SmartRedirect />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/dashboard"
                            element={
                                <ProtectedRoute requiredModule="dashboard" requiredAction="viewDashboard">
                                    <Dashboard />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/users/accounts"
                            element={
                                <ProtectedRoute requiredModule="user" requiredAction="viewUser">
                                    <Accounts />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/users/roles"
                            element={
                                <ProtectedRoute requiredModule="role" requiredAction="viewRole">
                                    <Roles />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/users/approval"
                            element={
                                <ProtectedRoute requiredModule="approvalaccount" requiredAction="viewApprovalAccount">
                                    <ApprovalAccounts />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/users/account-management"
                            element={
                                <ProtectedRoute requiredModule="user" requiredAction="viewUser">
                                    <AccountManagement />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/catalog/units"
                            element={
                                <ProtectedRoute requiredModule="unit" requiredAction="viewUnit">
                                    <Units />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/catalog/vehicles"
                            element={
                                <ProtectedRoute requiredModule="vehicle" requiredAction="viewVehicle">
                                    <Vehicles />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/catalog/entry-exit-reasons"
                            element={
                                <ProtectedRoute requiredModule="entryexitreason" requiredAction="viewEntryExitReason">
                                    <EntryExitReasons />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/catalog/positions"
                            element={
                                <ProtectedRoute requiredModule="position" requiredAction="viewPosition">
                                    <Positions />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/catalog/visit-types"
                            element={
                                <ProtectedRoute requiredModule="visittype" requiredAction="viewVisitType">
                                    <VisitTypes />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/catalog/identity-types"
                            element={
                                <ProtectedRoute requiredModule="identitytype" requiredAction="viewIdentityType">
                                    <IdentityTypes />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/catalog/verify-modes"
                            element={
                                <ProtectedRoute requiredModule="verifymode" requiredAction="viewVerifyMode">
                                    <VerifyModes />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/license-management/licenses"
                            element={
                                <ProtectedRoute requiredModule="license" requiredAction="viewLicense">
                                    <Licenses />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/license-management/shared-access-management"
                            element={
                                <ProtectedRoute requiredModule="license" requiredAction="viewLicense">
                                    <SharedAccessManagement />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/license-management/firmware-versions"
                            element={
                                <ProtectedRoute requiredModule="firmwareversion" requiredAction="viewFirmwareVersion">
                                    <FirmwareVersions />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/license-management/units"
                            element={
                                <ProtectedRoute requiredModule="unit" requiredAction="viewUnit">
                                    <LicenseUnits />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/devices"
                            element={
                                <ProtectedRoute requiredModule="device" requiredAction="viewDevice">
                                    <Devices />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/devices/configuration"
                            element={
                                <ProtectedRoute requiredModule="devicetype" requiredAction="viewDeviceType">
                                    <DeviceConfiguration />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/reports/access"
                            element={
                                <ProtectedRoute requiredModule="report" requiredAction="viewAccessReport">
                                    <AccessReport />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/reports/devices"
                            element={
                                <ProtectedRoute requiredModule="report" requiredAction="viewDeviceReport">
                                    <DeviceReport />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/reports/database-analysis"
                            element={
                                <ProtectedRoute requiredModule="databaseanalysis" requiredAction="viewDatabaseAnalysis">
                                    <DatabaseAnalysis />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/reports/violation-statistics"
                            element={
                                <ProtectedRoute requiredModule="report" requiredAction="viewViolationReport">
                                    <ViolationStatistics />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/reports/backup-logs"
                            element={
                                <ProtectedRoute requiredModule="systemlog" requiredAction="viewSystemLog">
                                    <BackupLogs />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/statistics/vneid"
                            element={
                                <ProtectedRoute requiredModule="setting" requiredAction="viewSetting">
                                    <VNeIDStatistics />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/reports/lgsp-integration"
                            element={
                                <ProtectedRoute requiredModule="systemlog" requiredAction="viewSystemLog">
                                    <LGSPIntegration />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/system/settings"
                            element={
                                <ProtectedRoute requiredModule="setting" requiredAction="viewSetting">
                                    <Settings />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/system/logs"
                            element={
                                <ProtectedRoute requiredModule="systemlog" requiredAction="viewSystemLog">
                                    <SystemLogs />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/system/alert-configurations"
                            element={
                                <ProtectedRoute requiredModule="setting" requiredAction="viewSetting">
                                    <AlertConfigurations />
                                </ProtectedRoute>
                            }
                        />

                        {/* Backup & Recovery Management Routes */}
                        <Route
                            path="/backup/sync-data"
                            element={
                                <ProtectedRoute requiredModule="backup" requiredAction="viewBackup">
                                    <SyncData />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/backup/system"
                            element={
                                <ProtectedRoute requiredModule="backup" requiredAction="viewBackup">
                                    <SystemBackup />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/backup/units"
                            element={
                                <ProtectedRoute requiredModule="backup" requiredAction="viewBackup">
                                    <UnitsBackup />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/backup/btl86"
                            element={
                                <ProtectedRoute requiredModule="backup" requiredAction="viewBackup">
                                    <BTL86Backup />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/backup/logs"
                            element={
                                <ProtectedRoute requiredModule="backup" requiredAction="viewBackup">
                                    <BackupLogsPage />
                                </ProtectedRoute>
                            }
                        />

                        {/* Catch-all route for undefined URLs - must be last */}
                        <Route path="*" element={<NotFound />} />
                    </Routes>
                </div>
            </div>
        </div>
    );
}

function App() {
    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <NotificationProvider>
                <Router>
                    <AppContent />
                </Router>
            </NotificationProvider>
        </ThemeProvider>
    );
}

export default App;
